import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { ChevronLeft, Send } from 'lucide-react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ChatPage = () => {
    const { conversationId } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [conversation, setConversation] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        fetchMessages();
        const interval = setInterval(fetchMessages, 3000); // Poll for new messages
        return () => clearInterval(interval);
    }, [conversationId]);

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const fetchMessages = async () => {
        try {
            const [convRes, msgRes] = await Promise.all([
                axios.get(`${API}/messages/conversations`),
                axios.get(`${API}/messages/conversations/${conversationId}`)
            ]);
            
            const conv = convRes.data.find(c => c.id === conversationId);
            setConversation(conv);
            setMessages(msgRes.data);
        } catch (error) {
            console.error('Error fetching messages:', error);
        } finally {
            setLoading(false);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const handleSend = async () => {
        if (!newMessage.trim() || sending) return;

        setSending(true);
        try {
            const response = await axios.post(`${API}/messages/conversations/${conversationId}`, {
                content: newMessage.trim()
            });
            setMessages(prev => [...prev, response.data]);
            setNewMessage('');
        } catch (error) {
            console.error('Error sending message:', error);
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const getOtherParticipant = () => {
        if (!conversation) return null;
        return conversation.participants.find(p => p.id !== user.id);
    };

    const otherUser = getOtherParticipant();

    if (loading) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
        );
    }

    return (
        <div className="h-screen bg-black flex flex-col" data-testid="chat-page">
            {/* Header */}
            <div className="glass border-b border-white/10 flex-shrink-0">
                <div className="max-w-md mx-auto flex items-center gap-3 px-2 h-14">
                    <button
                        onClick={() => navigate('/messages')}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
                        data-testid="back-button"
                    >
                        <ChevronLeft className="w-6 h-6 text-white" />
                    </button>
                    {otherUser && (
                        <button
                            onClick={() => navigate(`/profile/${otherUser.id}`)}
                            className="flex items-center gap-3"
                        >
                            <Avatar className="w-9 h-9">
                                <AvatarImage src={otherUser.avatar} alt={otherUser.username} />
                                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                                    {otherUser.username?.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            </Avatar>
                            <span className="text-white font-medium">{otherUser.username}</span>
                        </button>
                    )}
                </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4" data-testid="messages-container">
                <div className="max-w-md mx-auto space-y-3">
                    {messages.map((message, index) => {
                        const isOwn = message.sender_id === user.id;
                        return (
                            <motion.div
                                key={message.id}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.02 }}
                                className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                            >
                                <div
                                    className={`max-w-[75%] px-4 py-2 rounded-2xl ${
                                        isOwn
                                            ? 'bg-primary text-white rounded-br-md'
                                            : 'bg-surface text-white rounded-bl-md'
                                    }`}
                                    data-testid={`message-${message.id}`}
                                >
                                    <p className="text-sm">{message.content}</p>
                                    <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-white/40'}`}>
                                        {formatDistanceToNow(new Date(message.created_at), { addSuffix: true })}
                                    </p>
                                </div>
                            </motion.div>
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div>
            </div>

            {/* Input */}
            <div className="glass border-t border-white/10 p-4 flex-shrink-0 safe-bottom">
                <div className="max-w-md mx-auto flex gap-2">
                    <Input
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        onKeyPress={handleKeyPress}
                        placeholder="Type a message..."
                        className="flex-1 bg-surface border-border text-white placeholder:text-white/40"
                        data-testid="message-input"
                    />
                    <Button
                        onClick={handleSend}
                        disabled={!newMessage.trim() || sending}
                        className="bg-primary hover:bg-primary-hover px-4"
                        data-testid="send-button"
                    >
                        <Send className="w-5 h-5" />
                    </Button>
                </div>
            </div>
        </div>
    );
};

export default ChatPage;
